import { MainLayout } from '@/components/layout/MainLayout';
import { CatalogMenu } from '@/components/catalog/CatalogMenu';
import { ImportPanel, type ImportableProduct } from '@/components/catalog/ImportPanel';
import { useState, useEffect, useMemo, useCallback } from 'react';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '@/lib/api/client';
import { useAuth } from '@/lib/hooks/useAuth';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors, DragOverlay,
  type DragEndEvent, type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Product {
  id: number;
  name: string;
  sku: string;
  description: string | null;
  unit_price: number;
  unit_of_measure: string;
  current_stock: number | null;
  reorder_point: number | null;
  supplier_name: string | null;
  lead_time_days: number | null;
  category_name?: string;
  source_url?: string;
}

interface Client {
  id: number;
  name: string;
}

interface Job {
  id: number;
  title: string;
  name?: string;
  clientId: number;
  client_id?: number; // Fallback for different API formats
}

const CART_STORAGE_KEY = 'catalog_cart';
const CLIENT_STORAGE_KEY = 'catalog_selected_client';
const JOB_STORAGE_KEY = 'catalog_selected_job';

// Initialize cart from localStorage — defined outside component to avoid recreation every render
const initializeCart = (): Map<number, { product: Product; quantity: number }> => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Map(parsed.map((item: any) => [item.productId, { product: item.product, quantity: item.quantity }]));
    }
  } catch {
    // ignore storage errors
  }
  return new Map();
};

/**
 * Parse a dimensional search query like "2x4x10" or 2"x4"x10' into numeric tokens.
 * Returns null if the query is not a dimensional pattern.
 */
function parseDimensionalQuery(raw: string): number[] | null {
  // Strip units and quote chars
  const cleaned = raw
    .replace(/['"]/g, '')          // remove " and '
    .replace(/\b(ft|in)\b/gi, '') // remove ft/in words
    .replace(/\s+/g, ' ')
    .trim();
  // Must look like: N x N  or  N x N x N  (2+ numbers separated by x)
  if (!/^\d+(\.\d+)?(\s*[xX]\s*\d+(\.\d+)?){1,3}$/.test(cleaned)) return null;
  const parts = cleaned.split(/\s*[xX]\s*/);
  const nums = parts.map(p => parseFloat(p)).filter(n => !isNaN(n) && n > 0);
  return nums.length >= 2 ? nums : null;
}

function dimensionalMatch(name: string, dims: number[]): boolean {
  return dims.every(dim => {
    // Match whole numbers as word boundaries
    const intStr = String(Math.round(dim));
    return new RegExp(`\\b${intStr}\\b`).test(name);
  });
}

interface ProductEditModalProps {
  product: Product;
  saving: boolean;
  onSave: (updated: Partial<Product>) => void;
  onClose: () => void;
}

const ProductEditModal = ({ product, saving, onSave, onClose }: ProductEditModalProps) => {
  const [name, setName] = useState(product.name);
  const [sku, setSku] = useState(product.sku ?? '');
  const [price, setPrice] = useState(String(product.unit_price));
  const [uom, setUom] = useState(product.unit_of_measure ?? '');
  const [sourceUrl, setSourceUrl] = useState(product.source_url ?? '');

  const inputCls = 'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Edit Product</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Product Name</label>
            <input className={inputCls} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">SKU</label>
              <input className={inputCls} value={sku} onChange={e => setSku(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Unit Price ($)</label>
              <input className={inputCls} type="number" step="0.01" min="0" value={price} onChange={e => setPrice(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Unit of Measure</label>
            <input className={inputCls} value={uom} onChange={e => setUom(e.target.value)} placeholder="e.g. each, linear ft, sheet" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Source URL</label>
            <input className={inputCls} value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} placeholder="https://..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button
            disabled={saving}
            onClick={() => onSave({ name, sku: sku || undefined, unit_price: parseFloat(price) || 0, unit_of_measure: uom || undefined, source_url: sourceUrl || undefined })}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProductRowProps {
  product: Product;
  quantity: number;
  onQuantityChange: (id: number, value: number) => void;
  onAddToCart: (product: Product) => void;
  onSkuClick: (url: string | undefined) => void;
  onEdit: (product: Product) => void;
  isLast: boolean;
}

const ProductRow = React.memo(({ product, quantity, onQuantityChange, onAddToCart, onSkuClick, onEdit, isLast }: ProductRowProps) => (
  <tr key={product.id} className={isLast ? 'border-b-0' : 'border-b border-slate-200'}>
    <td className="p-1 text-slate-600 whitespace-nowrap">{product.category_name || '-'}</td>
    <td className="pl-2 pr-6 py-4">
      <div className="font-medium text-slate-800">{product.name}</div>
      <div className="text-xs text-slate-500">
        {product.supplier_name && <span>{product.supplier_name} - </span>}
        {product.source_url ? (
          <button onClick={() => onSkuClick(product.source_url)} className="text-primary hover:underline cursor-pointer">
            {product.sku}
          </button>
        ) : product.sku}
      </div>
    </td>
    <td className="px-4 py-4 text-slate-600 text-right whitespace-nowrap">
      ${Number(product.unit_price).toFixed(2)}
    </td>
    <td className="px-4 py-4 text-right">
      <input
        type="number" min="0"
        value={quantity}
        onChange={(e) => onQuantityChange(product.id, parseInt(e.target.value) || 0)}
        className="w-20 px-2 py-1.5 text-right border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
    </td>
    <td className="px-4 py-4 text-right">
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={() => onEdit(product)}
          className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
          title="Edit product"
        >
          <span className="material-symbols-outlined text-base">edit</span>
        </button>
        <button
          onClick={() => onAddToCart(product)}
          disabled={!quantity || quantity === 0}
          className="p-2 text-slate-500 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-base">shopping_cart</span>
        </button>
      </div>
    </td>
  </tr>
));
ProductRow.displayName = 'ProductRow';

// ── Cart order ────────────────────────────────────────────────────────────────

const CART_ORDER_KEY = 'catalog_cart_order';

type CartOrderEntry =
  | { type: 'item'; productId: number }
  | { type: 'heading'; id: string; text: string; level: 1 | 2 };

function getEntryId(e: CartOrderEntry): UniqueIdentifier {
  return e.type === 'item' ? `item-${e.productId}` : e.id;
}

const initializeCartOrder = (): CartOrderEntry[] => {
  const initial = initializeCart();
  try {
    const stored = localStorage.getItem(CART_ORDER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Array<{ type: 'item'; productId: number } | { type: 'heading'; id: string; text: string; level?: number }>;
      const valid: CartOrderEntry[] = parsed
        .filter(e => e.type === 'heading' || initial.has((e as { type: 'item'; productId: number }).productId))
        .map(e => e.type === 'heading'
          ? { type: 'heading' as const, id: e.id, text: e.text, level: (e.level === 2 ? 2 : 1) as 1 | 2 }
          : { type: 'item' as const, productId: (e as { type: 'item'; productId: number }).productId }
        );
      const seen = new Set<number>(valid.filter(e => e.type === 'item').map(e => (e as { type: 'item'; productId: number }).productId));
      const missing = Array.from(initial.keys()).filter(id => !seen.has(id)).map(id => ({ type: 'item' as const, productId: id }));
      return [...valid, ...missing];
    }
  } catch { /* ignore */ }
  return Array.from(initial.keys()).map(id => ({ type: 'item' as const, productId: id }));
};

// ── Sortable cart row ─────────────────────────────────────────────────────────

interface SortableCartItemProps {
  productId: number;
  product: Product;
  quantity: number;
  dbPrice?: number;
  hasPriceWarning: boolean;
  isEditingPrice: boolean;
  editingPriceValue: string;
  compact?: boolean;
  onQuantityChange: (qty: number) => void;
  onDelete: () => void;
  onStartEditPrice: (val: string) => void;
  onSetEditingValue: (v: string) => void;
  onApplyPrice: (v: string) => void;
  onCancelEditPrice: () => void;
  onAcceptDbPrice: () => void;
  onDismissDbPrice: () => void;
}

const SortableCartItem = ({
  productId, product, quantity, dbPrice, hasPriceWarning,
  isEditingPrice, editingPriceValue, compact,
  onQuantityChange, onDelete, onStartEditPrice, onSetEditingValue,
  onApplyPrice, onCancelEditPrice, onAcceptDbPrice, onDismissDbPrice,
}: SortableCartItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `item-${productId}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };
  const storedPrice = Number(product.unit_price);

  if (compact) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg group ${hasPriceWarning ? 'border border-amber-200 bg-amber-50/40' : 'border border-slate-200 bg-white'}`}
      >
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 text-slate-200 hover:text-slate-400 cursor-grab active:cursor-grabbing touch-none select-none opacity-0 group-hover:opacity-100 transition-opacity"
          tabIndex={-1}
          aria-label="Drag to reorder"
        >
          <span className="material-symbols-outlined text-sm">drag_indicator</span>
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
          <span className="text-sm font-medium text-slate-800 truncate">{product.name}</span>
          {hasPriceWarning && <span className="material-symbols-outlined text-amber-400 text-xs flex-shrink-0" title="Price changed in catalog">price_change</span>}
        </div>
        <span className="text-xs text-slate-400 flex-shrink-0 hidden sm:block">${storedPrice.toFixed(2)}</span>
        <input
          type="number"
          min="0"
          value={quantity}
          onChange={e => onQuantityChange(parseInt(e.target.value) || 0)}
          className="w-14 px-1.5 py-0.5 text-right border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary flex-shrink-0"
        />
        <span className="w-20 text-right text-sm font-semibold text-slate-700 flex-shrink-0">
          ${(storedPrice * quantity).toFixed(2)}
        </span>
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-opacity flex-shrink-0"
        >
          <span className="material-symbols-outlined text-sm">close</span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-3 p-4 border rounded-lg ${hasPriceWarning ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-white'}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 self-start pt-0.5 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none select-none"
        tabIndex={-1}
        aria-label="Drag to reorder"
      >
        <span className="material-symbols-outlined text-xl">drag_indicator</span>
      </button>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900">{product.name}</div>
        <div className="text-sm text-slate-500">
          {product.supplier_name && <span>{product.supplier_name} · </span>}
          {product.sku}
        </div>
        {isEditingPrice ? (
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-slate-500 font-medium">$</span>
            <input
              autoFocus
              type="number"
              step="0.01"
              min="0"
              value={editingPriceValue}
              onChange={e => onSetEditingValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') onApplyPrice(editingPriceValue);
                if (e.key === 'Escape') onCancelEditPrice();
              }}
              className="w-24 px-2 py-0.5 text-sm border border-primary rounded focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button onClick={() => onApplyPrice(editingPriceValue)} className="px-2 py-0.5 bg-primary text-white text-xs rounded hover:opacity-90">Set</button>
            <button onClick={onCancelEditPrice} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
          </div>
        ) : (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-sm text-slate-600">${storedPrice.toFixed(2)} each</span>
            <button
              onClick={() => onStartEditPrice(storedPrice.toFixed(2))}
              className="text-slate-400 hover:text-primary transition-colors"
              title="Override price"
            >
              <span className="material-symbols-outlined text-[14px]">edit</span>
            </button>
          </div>
        )}
        {hasPriceWarning && dbPrice !== undefined && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="material-symbols-outlined text-amber-500 text-sm">price_change</span>
            <span className="text-xs text-amber-700">
              Price changed: was ${storedPrice.toFixed(2)}, now ${dbPrice.toFixed(2)}
            </span>
            <button onClick={onAcceptDbPrice} className="px-2 py-0.5 rounded bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600">
              Use ${dbPrice.toFixed(2)}
            </button>
            <button onClick={onDismissDbPrice} className="text-xs text-amber-600 hover:underline">Keep ${storedPrice.toFixed(2)}</button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-shrink-0 self-start">
        <input
          type="number"
          min="0"
          value={quantity}
          onChange={e => onQuantityChange(parseInt(e.target.value) || 0)}
          className="w-20 px-2 py-1.5 text-right border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <div className="w-24 text-right font-medium text-slate-900">
          ${(storedPrice * quantity).toFixed(2)}
        </div>
        <button
          onClick={onDelete}
          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <span className="material-symbols-outlined text-base">delete</span>
        </button>
      </div>
    </div>
  );
};

// ── Sortable heading ──────────────────────────────────────────────────────────

interface SortableCartHeadingProps {
  heading: { id: string; text: string; level: 1 | 2 };
  isEditing: boolean;
  editingText: string;
  sectionTotal: number;
  compact?: boolean;
  onStartEdit: (text: string) => void;
  onSetText: (v: string) => void;
  onSaveEdit: () => void;
  onDelete: () => void;
}

const SortableCartHeading = ({ heading, isEditing, editingText, sectionTotal, compact, onStartEdit, onSetText, onSaveEdit, onDelete }: SortableCartHeadingProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: heading.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };
  const isL1 = heading.level === 1;

  return (
    <div ref={setNodeRef} style={style} className={`group ${isL1 ? (compact ? 'mt-2 mb-0' : 'mt-3 mb-0.5') : 'mt-0.5'}`}>
      {isL1 && <div className="h-px bg-slate-200 mb-1.5" />}
      <div className={`flex items-center gap-2 py-1 ${isL1 ? 'px-2' : 'px-2 pl-7'}`}>
        <button
          {...attributes}
          {...listeners}
          className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none select-none flex-shrink-0"
          tabIndex={-1}
        >
          <span className="material-symbols-outlined text-base">drag_indicator</span>
        </button>
        {isEditing ? (
          <input
            autoFocus
            value={editingText}
            onChange={e => onSetText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') onSaveEdit(); }}
            onBlur={onSaveEdit}
            className={`flex-1 bg-transparent border-b border-primary focus:outline-none pb-0.5 ${
              isL1 ? 'font-bold text-xs text-slate-600 uppercase tracking-widest' : 'font-semibold text-xs text-slate-500 tracking-wide'
            }`}
            placeholder="Section name…"
          />
        ) : (
          <button
            onClick={() => onStartEdit(heading.text)}
            className={`flex-1 text-left transition-colors ${
              isL1
                ? 'font-bold text-xs text-slate-500 uppercase tracking-widest hover:text-slate-700'
                : 'font-medium text-xs text-slate-400 italic tracking-wide hover:text-slate-500'
            }`}
          >
            {heading.text}
          </button>
        )}
        {sectionTotal > 0 && (
          <span className={`text-xs font-semibold flex-shrink-0 mr-1 ${isL1 ? 'text-primary' : 'text-slate-400'}`}>
            ${sectionTotal.toFixed(2)}
          </span>
        )}
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity p-1 rounded flex-shrink-0"
        >
          <span className="material-symbols-outlined text-sm">close</span>
        </button>
      </div>
    </div>
  );
};

export const CatalogPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, accessToken } = useAuth();
  const { toast, toasts, removeToast } = useToast();

  const [cart, setCart] = useState<Map<number, { product: Product; quantity: number }>>(initializeCart());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [quantities, setQuantities] = useState<Map<number, number>>(new Map());
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);

  // Initialize client and job from localStorage
  const [selectedClientId, setSelectedClientId] = useState<number | null>(() => {
    const stored = localStorage.getItem(CLIENT_STORAGE_KEY);
    return stored ? parseInt(stored) : null;
  });
  const [selectedJobId, setSelectedJobId] = useState<number | null>(() => {
    const stored = localStorage.getItem(JOB_STORAGE_KEY);
    return stored ? parseInt(stored) : null;
  });

  // Parse category path from URL
  const getCategoryFromUrl = (): string[] => {
    const path = location.pathname.replace('/catalog', '').replace(/^\//, '');
    if (!path) return [];
    return path.split('/').map(decodeURIComponent);
  };

  const [selectedCategory, setSelectedCategory] = useState<string[]>(getCategoryFromUrl());
  const [products, setProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<'category' | 'name' | 'price'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Bulk price update state
  const [bulkPriceMode, setBulkPriceMode] = useState(false);
  const [bulkPctStr, setBulkPctStr] = useState('');
  const [bulkPriceApplying, setBulkPriceApplying] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [cartOrder, setCartOrder] = useState<CartOrderEntry[]>(initializeCartOrder);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [editingHeadingId, setEditingHeadingId] = useState<string | null>(null);
  const [editingHeadingText, setEditingHeadingText] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [priceCheckData, setPriceCheckData] = useState<Map<number, number>>(new Map());
  const [priceChecking, setPriceChecking] = useState(false);
  const [priceCheckDismissed, setPriceCheckDismissed] = useState<Set<number>>(new Set());
  const [editingCartPriceId, setEditingCartPriceId] = useState<number | null>(null);
  const [editingCartPriceValue, setEditingCartPriceValue] = useState('');
  const [isCartCompact, setIsCartCompact] = useState(() => localStorage.getItem('catalog_cart_compact') === '1');
  const [appendMode, setAppendMode] = useState(false);
  const [existingEstimates, setExistingEstimates] = useState<Array<{ id: number; title: string }>>([]);
  const [selectedEstimateId, setSelectedEstimateId] = useState<number | null>(null);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      const cartArray = Array.from(cart.entries()).map(([productId, { product, quantity }]) => ({
        productId,
        product,
        quantity
      }));
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartArray));
    } catch {
      // ignore storage errors
    }
  }, [cart]);

  useEffect(() => {
    try { localStorage.setItem(CART_ORDER_KEY, JSON.stringify(cartOrder)); } catch { /* ignore */ }
  }, [cartOrder]);

  useEffect(() => {
    localStorage.setItem('catalog_cart_compact', isCartCompact ? '1' : '0');
  }, [isCartCompact]);

  // Save selected client to localStorage
  useEffect(() => {
    if (selectedClientId !== null) {
      localStorage.setItem(CLIENT_STORAGE_KEY, selectedClientId.toString());
    } else {
      localStorage.removeItem(CLIENT_STORAGE_KEY);
    }
  }, [selectedClientId]);

  // Save selected job to localStorage
  useEffect(() => {
    if (selectedJobId !== null) {
      localStorage.setItem(JOB_STORAGE_KEY, selectedJobId.toString());
    } else {
      localStorage.removeItem(JOB_STORAGE_KEY);
    }
  }, [selectedJobId]);

  // Fetch existing estimates when append mode is active and a job is selected
  useEffect(() => {
    if (!appendMode || !selectedJobId) {
      setExistingEstimates([]);
      setSelectedEstimateId(null);
      return;
    }
    apiClient.get('/estimates', { params: { job_id: selectedJobId } })
      .then(res => {
        setExistingEstimates(res.data?.data || []);
        setSelectedEstimateId(null);
      })
      .catch(() => setExistingEstimates([]));
  }, [appendMode, selectedJobId]);

  // Fetch clients and jobs
  useEffect(() => {
    const fetchClientsAndJobs = async () => {
      if (!isAuthenticated || !accessToken) {
        return;
      }

      try {
        const [clientsRes, jobsRes] = await Promise.all([
          apiClient.get('/clients'),
          apiClient.get('/jobs')
        ]);

        // Extract data from response
        // Clients API returns { success: true, data: [...] }
        const clientsData = clientsRes.data?.data || clientsRes.data || [];

        // Jobs API returns { success: true, data: { jobs: [...], total, page, ... } }
        const jobsData = jobsRes.data?.data?.jobs || jobsRes.data?.jobs || jobsRes.data || [];

        setClients(Array.isArray(clientsData) ? clientsData : []);
        setJobs(Array.isArray(jobsData) ? jobsData : []);
      } catch {
        // Set empty arrays on error to prevent map errors
        setClients([]);
        setJobs([]);
      }
    };

    fetchClientsAndJobs();
  }, [isAuthenticated, accessToken]);
  const [error, setError] = useState<string | null>(null);

  // Update selected category when URL changes
  useEffect(() => {
    const newCategory = getCategoryFromUrl();
    setSelectedCategory(newCategory);
  }, [location.pathname]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      // CR #186: /api/catalog sits behind auth (staffOnly) since the security
      // audit — a raw fetch() has no Authorization header and 401s. Go through
      // apiClient so the JWT (and refresh flow) is applied.
      let url = '/catalog/products';

      if (selectedCategory.length > 0) {
        const categoryName = selectedCategory[selectedCategory.length - 1];
        url = `/catalog/products/category/${encodeURIComponent(categoryName)}`;
      }

      const response = await apiClient.get(url);
      setProducts(response.data);
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Debounce search to avoid filtering on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 150);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const filteredProducts = useMemo(() => {
    if (!debouncedQuery.trim()) return products;
    const keywords = debouncedQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length === 0) return products;
    return products.filter(p => {
      const text = `${p.name} ${p.sku ?? ''} ${p.description ?? ''} ${p.category_name ?? ''}`.toLowerCase();
      return keywords.every(kw => {
        const dims = parseDimensionalQuery(kw);
        return dims ? dimensionalMatch(p.name, dims) : text.includes(kw);
      });
    });
  }, [products, debouncedQuery]);

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'category': aVal = a.category_name || ''; bVal = b.category_name || ''; break;
        case 'price':    aVal = Number(a.unit_price) || 0; bVal = Number(b.unit_price) || 0; break;
        default:         aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sortField, sortDirection]);

  // Incremental rendering: the catalog can hold thousands of products, and mounting
  // every row at once is what makes the page (and search) sluggish. We only render a
  // window of rows and grow it as the user scrolls toward the bottom.
  const ROW_PAGE_SIZE = 200;
  const [visibleCount, setVisibleCount] = useState(ROW_PAGE_SIZE);

  // Reset the window whenever the result set changes (new search, sort, or category).
  useEffect(() => {
    setVisibleCount(ROW_PAGE_SIZE);
  }, [debouncedQuery, sortField, sortDirection, products]);

  const visibleProducts = useMemo(
    () => sortedProducts.slice(0, visibleCount),
    [sortedProducts, visibleCount]
  );

  const handleProductsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    // Load the next page when scrolled within ~600px of the bottom.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 600) {
      setVisibleCount(c => (c < sortedProducts.length ? c + ROW_PAGE_SIZE : c));
    }
  }, [sortedProducts.length]);

  const handleCategorySelect = (category: string[]) => {
    // Update URL when category changes
    const path = category.map(encodeURIComponent).join('/');
    navigate(`/catalog/${path}`);
  };

  const handleBreadcrumbClick = (index: number) => {
    // Navigate to parent category when clicking breadcrumb
    const newCategory = selectedCategory.slice(0, index + 1);
    const path = newCategory.map(encodeURIComponent).join('/');
    navigate(`/catalog/${path}`);
  };

  const handleSkuClick = useCallback((url: string | undefined) => {
    if (url) window.open(url, '_blank', 'width=1200,height=900,resizable=yes,scrollbars=yes');
  }, []);

  const handleSaveEdit = async (updated: Partial<Product>) => {
    if (!editingProduct) return;
    setEditSaving(true);
    try {
      await apiClient.put(`/catalog/products/${editingProduct.id}`, updated);
      setProducts(prev => prev.map(p =>
        p.id === editingProduct.id ? { ...p, ...updated } : p
      ));
      setEditingProduct(null);
      toast.success('Product updated');
    } catch (err) {
      console.error('Failed to update product:', err);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  // Bulk price update: apply % change to all visible products
  const handleBulkPriceUpdate = async () => {
    const pct = parseFloat(bulkPctStr);
    if (isNaN(pct) || pct === 0) { toast.warning('Enter a non-zero percentage'); return; }
    const targets = sortedProducts.filter(p => Number(p.unit_price) > 0);
    if (targets.length === 0) { toast.warning('No products to update'); return; }
    if (!confirm(`Apply ${pct > 0 ? '+' : ''}${pct}% price change to ${targets.length} visible product${targets.length !== 1 ? 's' : ''}?`)) return;
    setBulkPriceApplying(true);
    let updated = 0;
    for (const p of targets) {
      const newPrice = Number(p.unit_price) * (1 + pct / 100);
      try {
        await apiClient.put(`/catalog/products/${p.id}`, { unit_price: parseFloat(newPrice.toFixed(2)) });
        updated++;
      } catch {}
    }
    await fetchProducts();
    setBulkPriceMode(false);
    setBulkPctStr('');
    setBulkPriceApplying(false);
    toast.success(`Updated prices for ${updated} product${updated !== 1 ? 's' : ''}`);
  };

  const handleCartDeleteItem = useCallback((productId: number) => {
    setCart(prev => { const n = new Map(prev); n.delete(productId); return n; });
    setCartOrder(prev => prev.filter(e => !(e.type === 'item' && e.productId === productId)));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      setCartOrder(prev => {
        const oldIdx = prev.findIndex(e => getEntryId(e) === active.id);
        const newIdx = prev.findIndex(e => getEntryId(e) === over.id);
        return oldIdx === -1 || newIdx === -1 ? prev : arrayMove(prev, oldIdx, newIdx);
      });
    }
  }, []);

  const addHeading = useCallback((level: 1 | 2 = 1) => {
    const id = `h-${Date.now()}`;
    const text = level === 1 ? 'Section' : 'Sub-section';
    setCartOrder(prev => [...prev, { type: 'heading', id, text, level }]);
    setEditingHeadingId(id);
    setEditingHeadingText(text);
  }, []);

  const handleImportToCart = useCallback((importProduct: ImportableProduct, qty: number) => {
    const product: Product = { ...importProduct, category_name: undefined };
    setCart(prev => {
      const n = new Map(prev);
      const existing = n.get(product.id);
      n.set(product.id, { product, quantity: (existing?.quantity ?? 0) + qty });
      return n;
    });
    setCartOrder(prev =>
      prev.some(e => e.type === 'item' && e.productId === product.id)
        ? prev
        : [...prev, { type: 'item', productId: product.id }]
    );
  }, []);

  const openCart = useCallback(async () => {
    setIsCartOpen(true);
    if (cart.size === 0) return;
    setPriceChecking(true);
    setPriceCheckDismissed(new Set());
    try {
      const ids = Array.from(cart.keys());
      const res = await apiClient.post('/catalog/products/price-check', { ids });
      const data: Array<{ id: number; unit_price: number }> = res.data?.data || [];
      const newMap = new Map<number, number>();
      data.forEach(p => newMap.set(p.id, Number(p.unit_price)));
      setPriceCheckData(newMap);
    } catch {
      // advisory only
    } finally {
      setPriceChecking(false);
    }
  }, [cart]);

  const priceChanges = useMemo(() => {
    const changes = new Map<number, number>();
    cart.forEach(({ product }) => {
      const dbPrice = priceCheckData.get(product.id);
      if (dbPrice !== undefined && Math.abs(dbPrice - Number(product.unit_price)) > 0.005 && !priceCheckDismissed.has(product.id)) {
        changes.set(product.id, dbPrice);
      }
    });
    return changes;
  }, [cart, priceCheckData, priceCheckDismissed]);

  const acceptPriceChange = useCallback((productId: number, dbPrice: number) => {
    setCart(prev => {
      const n = new Map(prev);
      const item = n.get(productId);
      if (item) n.set(productId, { product: { ...item.product, unit_price: dbPrice }, quantity: item.quantity });
      return n;
    });
    setPriceCheckDismissed(prev => { const s = new Set(prev); s.add(productId); return s; });
  }, []);

  const dismissPriceChange = useCallback((productId: number) => {
    setPriceCheckDismissed(prev => { const s = new Set(prev); s.add(productId); return s; });
  }, []);

  const acceptAllPriceChanges = useCallback(() => {
    const snapshot = new Map(priceChanges);
    setCart(prev => {
      const n = new Map(prev);
      snapshot.forEach((dbPrice, productId) => {
        const item = n.get(productId);
        if (item) n.set(productId, { product: { ...item.product, unit_price: dbPrice }, quantity: item.quantity });
      });
      return n;
    });
    setPriceCheckDismissed(prev => {
      const s = new Set(prev);
      snapshot.forEach((_, id) => s.add(id));
      return s;
    });
  }, [priceChanges]);

  const applyCartPriceOverride = useCallback((productId: number, value: string) => {
    const price = parseFloat(value);
    if (isNaN(price) || price < 0) return;
    setCart(prev => {
      const n = new Map(prev);
      const item = n.get(productId);
      if (item) n.set(productId, { product: { ...item.product, unit_price: price }, quantity: item.quantity });
      return n;
    });
    setPriceCheckDismissed(prev => { const s = new Set(prev); s.add(productId); return s; });
    setEditingCartPriceId(null);
    apiClient.put(`/catalog/products/${productId}`, { unit_price: price }).catch(() => {});
  }, []);

  const { totalItems, totalCost } = useMemo(() => {
    let totalItems = 0;
    let totalCost = 0;
    cart.forEach(({ product, quantity }) => {
      totalItems += 1;
      totalCost += Number(product.unit_price) * quantity;
    });
    return { totalItems, totalCost };
  }, [cart]);

  const sectionTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (let i = 0; i < cartOrder.length; i++) {
      const entry = cartOrder[i];
      if (entry.type !== 'heading') continue;
      let total = 0;
      for (let j = i + 1; j < cartOrder.length; j++) {
        const next = cartOrder[j];
        if (next.type === 'heading' && next.level <= entry.level) break;
        if (next.type === 'item') {
          const item = cart.get(next.productId);
          if (item) total += Number(item.product.unit_price) * item.quantity;
        }
      }
      totals.set(entry.id, total);
    }
    return totals;
  }, [cartOrder, cart]);

  const handleSort = (field: 'category' | 'name' | 'price') => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleQuantityChange = useCallback((productId: number, value: number) => {
    setQuantities(prev => { const n = new Map(prev); n.set(productId, value); return n; });
  }, []);

  const handleAddToCart = useCallback((product: Product) => {
    const qty = quantities.get(product.id) || 0;
    if (qty <= 0) return;
    setCart(prev => {
      const n = new Map(prev);
      const existing = n.get(product.id);
      n.set(product.id, { product, quantity: (existing?.quantity ?? 0) + qty });
      return n;
    });
    setCartOrder(prev =>
      prev.some(e => e.type === 'item' && e.productId === product.id)
        ? prev
        : [...prev, { type: 'item', productId: product.id }]
    );
    setQuantities(prev => { const n = new Map(prev); n.set(product.id, 0); return n; });
  }, [quantities]);

  const filteredJobs = useMemo(() =>
    selectedClientId
      ? jobs.filter(job => (job.clientId || job.client_id) === selectedClientId)
      : jobs,
    [jobs, selectedClientId]
  );

  const orderedEntryIds = useMemo(() => cartOrder.map(getEntryId), [cartOrder]);
  const activeEntry = useMemo(
    () => activeId != null ? (cartOrder.find(e => getEntryId(e) === activeId) ?? null) : null,
    [activeId, cartOrder]
  );

  return (
    <MainLayout catalogMenuPanel={<CatalogMenu onCategorySelect={handleCategorySelect} selectedCategory={selectedCategory} />} noPadding>
      <div className="flex flex-col h-full">
        {/* Header Section */}
        <div className="border-b border-slate-200 bg-white px-6 py-4">
          {/* Breadcrumbs and Cart */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <button
                onClick={() => navigate('/catalog')}
                className="hover:text-slate-900 transition-colors hover:underline"
              >
                All Items
              </button>
              {selectedCategory.length > 0 && selectedCategory.map((cat, index) => (
                <span key={index} className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs">chevron_right</span>
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className={`hover:text-slate-900 transition-colors ${
                      index === selectedCategory.length - 1 ? 'font-medium text-slate-900' : 'hover:underline'
                    }`}
                  >
                    {cat}
                  </button>
                </span>
              ))}
            </div>

            {/* Import + Cart buttons */}
            <div className="flex items-center gap-2">
            <button
              onClick={() => setIsImportOpen(v => !v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors font-medium text-sm ${
                isImportOpen
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="material-symbols-outlined text-base">add_link</span>
              Import by URL
            </button>
            <button
              onClick={openCart}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <span className="material-symbols-outlined text-base">shopping_cart</span>
              <span>({totalItems})</span>
              <span>${totalCost.toFixed(2)}</span>
              <span>Cart</span>
            </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white">
              <span className="material-symbols-outlined text-slate-400 text-base">search</span>
              <input
                className="min-w-[280px] border-0 bg-transparent text-sm outline-none focus:ring-0 placeholder:text-slate-400"
                placeholder="Search by name, SKU, or dimensions (e.g. 2x4x10)..."
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              )}
            </div>

            {/* Results count */}
            <span className="text-sm text-slate-500">
              {loading ? 'Loading...' : `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </span>

            <select className="rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
              <option value="">All Suppliers</option>
              <option value="Home Depot Canada">Home Depot Canada</option>
            </select>

            <select className="rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
              <option value="">Sort By</option>
              <option value="name_asc">Name (A-Z)</option>
              <option value="name_desc">Name (Z-A)</option>
              <option value="price_asc">Price (Low-High)</option>
              <option value="price_desc">Price (High-Low)</option>
            </select>

            {selectedCategory.length > 0 && (
              <button
                onClick={() => navigate('/catalog')}
                className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-base">clear</span>
                <span>Clear Filters</span>
              </button>
            )}

            {/* Bulk price update toggle */}
            <button
              onClick={() => setBulkPriceMode(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${bulkPriceMode ? 'bg-primary/10 border-primary text-primary' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
              title="Bulk price update for all visible products"
            >
              <span className="material-symbols-outlined text-base">price_change</span>
              Bulk Price
            </button>
            {bulkPriceMode && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                <span className="text-xs text-amber-700 font-medium">%</span>
                <input
                  type="number"
                  step="0.1"
                  value={bulkPctStr}
                  onChange={e => setBulkPctStr(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleBulkPriceUpdate(); if (e.key === 'Escape') { setBulkPriceMode(false); setBulkPctStr(''); } }}
                  placeholder="+5 or -3"
                  className="w-24 text-sm bg-transparent border-b border-amber-300 focus:outline-none text-amber-900"
                  autoFocus
                />
                <button
                  onClick={handleBulkPriceUpdate}
                  disabled={bulkPriceApplying || !bulkPctStr}
                  className="px-2 py-1 rounded bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50"
                >
                  {bulkPriceApplying ? '…' : 'Apply'}
                </button>
                <button onClick={() => { setBulkPriceMode(false); setBulkPctStr(''); }} className="text-amber-500 hover:text-amber-700">
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Import by URL panel */}
        {isImportOpen && (
          <ImportPanel onAddToCart={handleImportToCart} />
        )}

        {/* Content Section */}
        <div className="flex-1 overflow-auto" onScroll={handleProductsScroll}>
          <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-slate-600">Loading products...</div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-red-600">{error}</div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-slate-600">
                    {searchQuery ? `No products matching "${searchQuery}"` : 'No products found in this category'}
                  </div>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th
                        className="p-1 text-left text-sm font-semibold text-slate-700 w-1 whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('category')}
                      >
                        <div className="flex items-center gap-1">
                          Category
                          {sortField === 'category' && (
                            <span className="material-symbols-outlined text-sm">
                              {sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        className="pl-2 pr-6 py-4 text-left text-sm font-semibold text-slate-700 w-auto cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          Material
                          {sortField === 'name' && (
                            <span className="material-symbols-outlined text-sm">
                              {sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-4 text-right text-sm font-semibold text-slate-700 w-32 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('price')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Unit Cost
                          {sortField === 'price' && (
                            <span className="material-symbols-outlined text-sm">
                              {sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-4 text-right text-sm font-semibold text-slate-700 w-28">Qty</th>
                      <th className="px-4 py-4 text-right text-sm font-semibold text-slate-700 w-20">Cart</th>
                    </tr>
                  </thead>
                    <tbody>
                      {visibleProducts.map((product, index) => (
                        <ProductRow
                          key={product.id}
                          product={product}
                          quantity={quantities.get(product.id) ?? 0}
                          onQuantityChange={handleQuantityChange}
                          onAddToCart={handleAddToCart}
                          onSkuClick={handleSkuClick}
                          onEdit={setEditingProduct}
                          isLast={index === visibleProducts.length - 1}
                        />
                      ))}
                    </tbody>
                </table>
              )}
              {!loading && !error && visibleCount < sortedProducts.length && (
                <div className="flex items-center justify-center gap-3 py-4 border-t border-slate-100">
                  <span className="text-sm text-slate-500">
                    Showing {visibleProducts.length} of {sortedProducts.length}
                  </span>
                  <button
                    onClick={() => setVisibleCount(c => c + ROW_PAGE_SIZE)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Shopping Cart Popup Overlay */}
      {isCartOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsCartOpen(false)}
        >
          <div
            className="relative w-[800px] max-h-[80vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h3 className="text-xl font-semibold text-slate-900">Shopping Cart</h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsCartCompact(v => !v)}
                  className={`p-2 rounded-lg transition-colors ${isCartCompact ? 'text-primary bg-primary/10' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
                  title={isCartCompact ? 'Detailed view' : 'Compact view'}
                >
                  <span className="material-symbols-outlined text-base">{isCartCompact ? 'density_large' : 'density_small'}</span>
                </button>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
            </div>

            {/* Cart Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {cart.size === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <span className="material-symbols-outlined text-6xl mb-4">shopping_cart</span>
                  <p className="text-lg">Your cart is empty</p>
                  <p className="text-sm mt-2">Add items from the catalog to get started</p>
                </div>
              ) : (
                <div className={isCartCompact ? 'space-y-1' : 'space-y-3'}>
                  {/* Price check status banner */}
                  {priceChecking && (
                    <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-500">
                      <span className="material-symbols-outlined text-slate-400 text-base animate-spin">autorenew</span>
                      Checking current prices…
                    </div>
                  )}
                  {!priceChecking && priceChanges.size > 0 && (
                    <div className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
                      <span className="material-symbols-outlined text-amber-500 text-base">price_change</span>
                      <span className="flex-1 text-sm text-amber-800 font-medium">
                        {priceChanges.size} item{priceChanges.size > 1 ? 's have' : ' has'} a price update in the catalog
                      </span>
                      <button
                        onClick={acceptAllPriceChanges}
                        className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition-colors"
                      >
                        Update All
                      </button>
                    </div>
                  )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={({ active }) => setActiveId(active.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={orderedEntryIds} strategy={verticalListSortingStrategy}>
                      <div className={isCartCompact ? 'space-y-0.5' : 'space-y-1.5'}>
                        {cartOrder.map(entry => {
                          if (entry.type === 'heading') {
                            return (
                              <SortableCartHeading
                                key={entry.id}
                                heading={entry}
                                isEditing={editingHeadingId === entry.id}
                                editingText={editingHeadingText}
                                sectionTotal={sectionTotals.get(entry.id) ?? 0}
                                compact={isCartCompact}
                                onStartEdit={(text) => { setEditingHeadingId(entry.id); setEditingHeadingText(text); }}
                                onSetText={setEditingHeadingText}
                                onSaveEdit={() => {
                                  setCartOrder(prev => prev.map(e =>
                                    e.type === 'heading' && e.id === entry.id
                                      ? { ...e, text: editingHeadingText.trim() || e.text }
                                      : e
                                  ));
                                  setEditingHeadingId(null);
                                }}
                                onDelete={() => setCartOrder(prev => prev.filter(e => !(e.type === 'heading' && e.id === entry.id)))}
                              />
                            );
                          }
                          const item = cart.get(entry.productId);
                          if (!item) return null;
                          const { product, quantity } = item;
                          const dbPrice = priceChanges.get(product.id);
                          return (
                            <SortableCartItem
                              key={`item-${entry.productId}`}
                              productId={entry.productId}
                              product={product}
                              quantity={quantity}
                              dbPrice={dbPrice}
                              hasPriceWarning={dbPrice !== undefined}
                              isEditingPrice={editingCartPriceId === product.id}
                              editingPriceValue={editingCartPriceValue}
                              compact={isCartCompact}
                              onQuantityChange={qty => {
                                if (qty <= 0) {
                                  handleCartDeleteItem(product.id);
                                } else {
                                  setCart(prev => { const n = new Map(prev); n.set(product.id, { product, quantity: qty }); return n; });
                                }
                              }}
                              onDelete={() => handleCartDeleteItem(product.id)}
                              onStartEditPrice={val => { setEditingCartPriceId(product.id); setEditingCartPriceValue(val); }}
                              onSetEditingValue={setEditingCartPriceValue}
                              onApplyPrice={val => applyCartPriceOverride(product.id, val)}
                              onCancelEditPrice={() => setEditingCartPriceId(null)}
                              onAcceptDbPrice={() => dbPrice !== undefined && acceptPriceChange(product.id, dbPrice)}
                              onDismissDbPrice={() => dismissPriceChange(product.id)}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                    <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }}>
                      {activeEntry ? (
                        activeEntry.type === 'heading' ? (
                          <div className={`flex items-center gap-2 py-2 px-3 bg-white rounded-lg border border-slate-300 shadow-lg ${activeEntry.level === 2 ? 'ml-5' : ''}`}>
                            <span className="material-symbols-outlined text-slate-400 text-base">drag_indicator</span>
                            <span className={activeEntry.level === 1 ? 'font-bold text-xs text-slate-500 uppercase tracking-widest' : 'font-medium text-xs text-slate-400 italic tracking-wide'}>
                              {activeEntry.text}
                            </span>
                          </div>
                        ) : (() => {
                          const item = cart.get(activeEntry.productId);
                          if (!item) return null;
                          return (
                            <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-300 shadow-xl">
                              <span className="material-symbols-outlined text-slate-400 text-xl">drag_indicator</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-slate-900 truncate">{item.product.name}</div>
                                <div className="text-sm text-slate-500">
                                  ${Number(item.product.unit_price).toFixed(2)} · qty {item.quantity}
                                </div>
                              </div>
                            </div>
                          );
                        })()
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.size > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
                {/* Mode toggle */}
                <div className="flex rounded-lg border border-slate-200 bg-white mb-4 overflow-hidden text-sm font-medium">
                  <button
                    onClick={() => setAppendMode(false)}
                    className={`flex-1 px-3 py-2 transition-colors ${!appendMode ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    New Estimate
                  </button>
                  <button
                    onClick={() => setAppendMode(true)}
                    className={`flex-1 px-3 py-2 transition-colors ${appendMode ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    Append to Existing
                  </button>
                </div>

                {/* Client and Job Selection */}
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Client *
                    </label>
                    <select
                      value={selectedClientId || ''}
                      onChange={(e) => {
                        const clientId = e.target.value ? parseInt(e.target.value) : null;
                        setSelectedClientId(clientId);
                        setSelectedJobId(null);
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select a client...</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Job *
                    </label>
                    <select
                      value={selectedJobId || ''}
                      onChange={(e) => setSelectedJobId(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={!selectedClientId}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select a job...</option>
                      {filteredJobs.map(job => (
                        <option key={job.id} value={job.id}>
                          {job.title || job.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {appendMode && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Estimate *
                      </label>
                      <select
                        value={selectedEstimateId || ''}
                        onChange={(e) => setSelectedEstimateId(e.target.value ? parseInt(e.target.value) : null)}
                        disabled={!selectedJobId}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {selectedJobId
                            ? existingEstimates.length === 0
                              ? 'No estimates for this job'
                              : 'Select an estimate...'
                            : 'Select a job first...'}
                        </option>
                        {existingEstimates.map(est => (
                          <option key={est.id} value={est.id}>
                            {est.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-slate-600">
                    {totalItems} {totalItems === 1 ? 'item' : 'items'}
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    ${totalCost.toFixed(2)}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => addHeading(1)}
                    className="px-3 py-2 border border-dashed border-slate-300 text-slate-500 rounded-lg hover:bg-slate-100 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Add a section heading"
                  >
                    <span className="material-symbols-outlined text-base">title</span>
                    Heading
                  </button>
                  <button
                    onClick={() => addHeading(2)}
                    className="px-3 py-2 border border-dashed border-slate-300 text-slate-400 rounded-lg hover:bg-slate-100 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Add a sub-heading"
                  >
                    <span className="material-symbols-outlined text-base">format_indent_increase</span>
                    Sub-heading
                  </button>
                  <button
                    onClick={() => {
                      setCart(new Map());
                      setCartOrder([]);
                      localStorage.removeItem(CART_STORAGE_KEY);
                      localStorage.removeItem(CART_ORDER_KEY);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors font-medium"
                  >
                    Clear Cart
                  </button>
                  {appendMode ? (
                    <button
                      onClick={async () => {
                        if (!selectedClientId || !selectedJobId || !selectedEstimateId) {
                          toast.warning('Please select a client, job, and estimate.');
                          return;
                        }
                        try {
                          const existingRes = await apiClient.get(`/estimates/${selectedEstimateId}`);
                          const existingItems: any[] = existingRes.data?.items || [];
                          const cartItems = cartOrder
                            .filter(e => e.type === 'heading' || (e.type === 'item' && cart.has(e.productId)))
                            .map((e, idx) => {
                              if (e.type === 'heading') {
                                return { product_id: null, product_name: e.text, quantity: 0, unit_price: 0, total: 0, item_type: 'heading' as const, heading_level: e.level, sort_order: existingItems.length + idx };
                              }
                              const { product, quantity } = cart.get(e.productId)!;
                              return { product_id: product.id, product_name: product.name, quantity, unit_price: Number(product.unit_price), total: Number(product.unit_price) * quantity, item_type: 'item' as const, heading_level: 1 as const, sort_order: existingItems.length + idx };
                            });
                          await apiClient.put(`/estimates/${selectedEstimateId}`, {
                            items: [...existingItems, ...cartItems],
                          });
                          toast.success('Items appended to estimate successfully!');
                          setCart(new Map());
                          setCartOrder([]);
                          setSelectedClientId(null);
                          setSelectedJobId(null);
                          setSelectedEstimateId(null);
                          setAppendMode(false);
                          localStorage.removeItem(CART_STORAGE_KEY);
                          localStorage.removeItem(CART_ORDER_KEY);
                          localStorage.removeItem(CLIENT_STORAGE_KEY);
                          localStorage.removeItem(JOB_STORAGE_KEY);
                          setIsCartOpen(false);
                        } catch {
                          toast.error('An error occurred while appending to the estimate.');
                        }
                      }}
                      disabled={!selectedClientId || !selectedJobId || !selectedEstimateId}
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Append to Estimate
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!selectedClientId || !selectedJobId) {
                          toast.warning('Please select both a client and a job.');
                          return;
                        }
                        try {
                          const items = cartOrder
                            .filter(e => e.type === 'heading' || (e.type === 'item' && cart.has(e.productId)))
                            .map((e, idx) => {
                              if (e.type === 'heading') {
                                return {
                                  product_id: null as number | null,
                                  product_name: e.text,
                                  quantity: 0,
                                  unit_price: 0,
                                  total: 0,
                                  item_type: 'heading' as const,
                                  heading_level: e.level,
                                  sort_order: idx,
                                };
                              }
                              const { product, quantity } = cart.get(e.productId)!;
                              return {
                                product_id: product.id,
                                product_name: product.name,
                                quantity,
                                unit_price: Number(product.unit_price),
                                total: Number(product.unit_price) * quantity,
                                item_type: 'item' as const,
                                heading_level: 1 as const,
                                sort_order: idx,
                              };
                            });
                          await apiClient.post('/estimates', {
                            title: 'Material Estimate',
                            client_id: selectedClientId,
                            job_id: selectedJobId,
                            items,
                            total: totalCost,
                          });
                          toast.success('Estimate saved successfully!');
                          setCart(new Map());
                          setCartOrder([]);
                          setSelectedClientId(null);
                          setSelectedJobId(null);
                          localStorage.removeItem(CART_STORAGE_KEY);
                          localStorage.removeItem(CART_ORDER_KEY);
                          localStorage.removeItem(CLIENT_STORAGE_KEY);
                          localStorage.removeItem(JOB_STORAGE_KEY);
                          setIsCartOpen(false);
                        } catch {
                          toast.error('An error occurred while saving the estimate.');
                        }
                      }}
                      disabled={!selectedClientId || !selectedJobId}
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save as Estimate
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product Edit Modal */}
      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          saving={editSaving}
          onSave={handleSaveEdit}
          onClose={() => setEditingProduct(null)}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </MainLayout>
  );
};
